import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

// Components
import {
  Button,
  FieldSearchSelect,
  FieldSelect,
  FieldText,
  Toggle,
  ValidityDateRangePicker,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import BusinessValidityChangeNotice from "components/BusinessValidityChangeNotice";

// State
import {
  useGetEMSModuleSchemaQuery,
  useCreateEMSConfigMutation,
  useUpdateEMSConfigMutation,
  useDeleteEMSConfigMutation,
} from "state/communities/communitiesApiSlice";
import { useGetMetersByCommunityIdQuery } from "state/meters/metersApiSlice";
import {
  openNotificationPopup,
  openSuccessAlert,
  openWarningAlert,
} from "state/application/applicationSlice";
import { defaultBusinessEndDate, lisbonToday } from "utils/businessValidity";

/* Switch row matching the design: surface-2 card with title + hint on the
   left and a Toggle on the right. */
function ToggleRow({ label, hint, on, onToggle }) {
  return (
    <div
      className="flex items-center gap-3 cursor-pointer"
      style={{ padding: 14, background: "var(--surface-2)", borderRadius: "var(--radius-sm)" }}
      role="presentation"
      onClick={onToggle}
    >
      <div className="flex-1">
        <div className="text-[13.5px] font-semibold text-ink">{label}</div>
        {hint && <div className="text-[11.5px] text-ink-3 mt-[2px]">{hint}</div>}
      </div>
      <Toggle on={on} ariaLabel={typeof label === "string" ? label : undefined} />
    </div>
  );
}

ToggleRow.propTypes = {
  label: PropTypes.node.isRequired,
  hint: PropTypes.node,
  on: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

ToggleRow.defaultProps = { hint: null };

function ModuleConfigForm({ communityId, moduleType, config, isEditMode, onCancel, onSuccess }) {
  const { t } = useTranslation(["community", "common"]);
  const dispatch = useDispatch();

  // Fetch module schema
  const { data: schemaData, isLoading: isLoadingSchema } = useGetEMSModuleSchemaQuery({
    communityId,
    moduleType,
  });

  // Mutations
  const [createConfig, { isLoading: isCreating }] = useCreateEMSConfigMutation();
  const [updateConfig, { isLoading: isUpdating }] = useUpdateEMSConfigMutation();
  const [deleteConfig, { isLoading: isDeleting }] = useDeleteEMSConfigMutation();

  // Form state - Common fields
  const [horizon, setHorizon] = useState(config?.horizon || 24);
  const [deltaT, setDeltaT] = useState(config?.delta_t || 1.0);
  const [strictPosCoeffs, setStrictPosCoeffs] = useState(config?.strict_pos_coeffs ?? true);
  const [totalShareCoeffs, setTotalShareCoeffs] = useState(config?.total_share_coeffs ?? false);
  const initialStartDate = config?.start_date || lisbonToday();
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(
    config?.end_date || defaultBusinessEndDate(initialStartDate)
  );

  // Form state - Module-specific config_data
  const [configData, setConfigData] = useState(config?.config_data || {});

  const { data: metersData } = useGetMetersByCommunityIdQuery(
    { communityId },
    { skip: !communityId }
  );

  const meterOptions = useMemo(() => {
    const none = { value: "", label: t("unified_ems.meter_select_none") };
    if (!metersData?.ids) return [none];
    return [
      none,
      ...metersData.ids.map((id) => {
        const m = metersData.entities[id];
        const identifier = m?.identifier || id;
        const type = m?.type ? ` (${m.type})` : "";
        return { value: id, label: `${identifier}${type}` };
      }),
    ];
  }, [metersData, t]);

  const schema = schemaData?.config_schema;
  const properties = schema?.properties || {};
  const required = schema?.required || [];

  // Initialize config_data with default values from schema
  useEffect(() => {
    if (schema && !isEditMode) {
      const defaults = {};
      Object.entries(properties).forEach(([key, prop]) => {
        if (prop.default !== undefined) {
          defaults[key] = prop.default;
        }
      });
      setConfigData((prev) => ({ ...defaults, ...prev }));
    }
  }, [schema, isEditMode]);

  const handleConfigDataChange = (key, value) => {
    setConfigData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      if (!startDate || (endDate && endDate < startDate)) {
        throw new Error("The validity end must be on or after its start.");
      }
      const payload = {
        module_type: moduleType,
        horizon,
        delta_t: deltaT,
        strict_pos_coeffs: strictPosCoeffs,
        total_share_coeffs: totalShareCoeffs,
        config_data: configData,
        start_date: startDate,
        end_date: endDate || null,
      };

      if (isEditMode) {
        await updateConfig({
          communityId,
          configId: config.id,
          configData: {
            horizon,
            delta_t: deltaT,
            strict_pos_coeffs: strictPosCoeffs,
            total_share_coeffs: totalShareCoeffs,
            config_data: configData,
            start_date: startDate,
            end_date: endDate || null,
          },
        }).unwrap();

        dispatch(
          openSuccessAlert({
            title: t("unified_ems.update_success_title"),
            description: t("unified_ems.update_success_desc"),
          })
        );
        if (onSuccess) onSuccess();
      } else {
        await createConfig({ communityId, configData: payload }).unwrap();
        dispatch(
          openSuccessAlert({
            title: t("unified_ems.create_success_title"),
            description: t("unified_ems.create_success_desc"),
          })
        );
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      dispatch(
        openNotificationPopup({
          notification: error?.data?.detail || error.message || t("unified_ems.error_saving"),
          type: "error",
        })
      );
    }
  };

  const handleDelete = () => {
    dispatch(
      openWarningAlert({
        title: t("unified_ems.confirm_delete_title"),
        text: t("unified_ems.confirm_delete_text"),
        confirmText: t("unified_ems.confirm_delete_button"),
        isDangerous: true,
        icon: "warning",
        confirmAction: async () => {
          try {
            await deleteConfig({
              communityId,
              configId: config.id,
              effectiveAt: startDate,
              endDate: endDate || undefined,
            }).unwrap();
            dispatch(
              openSuccessAlert({
                title: t("unified_ems.delete_success_title"),
                description: t("unified_ems.delete_success_desc"),
              })
            );
            if (onCancel) onCancel();
          } catch (error) {
            dispatch(
              openNotificationPopup({
                notification:
                  error?.data?.detail || error.message || t("unified_ems.error_deleting"),
                type: "error",
              })
            );
          }
        },
      })
    );
  };

  const renderField = (key, prop) => {
    const value = configData[key] ?? "";
    const label = `${prop.description || key}${required.includes(key) ? " *" : ""}`;

    if (prop.type === "boolean") {
      return (
        <ToggleRow
          key={key}
          label={label}
          on={!!value}
          onToggle={() => handleConfigDataChange(key, !value)}
        />
      );
    }

    if (prop.type === "number") {
      return (
        <FieldText
          key={key}
          label={label}
          type="number"
          step="any"
          value={value}
          onChange={(v) => handleConfigDataChange(key, v === "" ? "" : Number(v))}
        />
      );
    }

    if (prop.format === "meter-select") {
      return (
        <FieldSearchSelect
          key={key}
          label={label}
          options={meterOptions}
          value={value}
          onChange={(v) => handleConfigDataChange(key, v)}
        />
      );
    }

    if (prop.enum) {
      return (
        <FieldSelect
          key={key}
          label={label}
          options={prop.enum.map((v) => ({ value: v, label: v }))}
          value={value}
          onChange={(v) => handleConfigDataChange(key, v)}
        />
      );
    }

    return (
      <FieldText
        key={key}
        label={label}
        value={value}
        onChange={(v) => handleConfigDataChange(key, v)}
      />
    );
  };

  if (isLoadingSchema) {
    return (
      <div className="flex justify-center py-8 text-ink-3">
        <span className="animate-spin inline-flex">
          <Icon name="progress_activity" style={{ fontSize: 28, color: "var(--accent)" }} />
        </span>
      </div>
    );
  }

  const busy = isCreating || isUpdating || isDeleting;

  return (
    <div>
      {/* Common settings */}
      <div className="rec-ems-section-title">{t("unified_ems.common_settings")}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px] items-end">
        <ValidityDateRangePicker
          className="sm:col-span-2"
          value={{ start: startDate, end: endDate }}
          onChange={({ start, end }) => {
            setStartDate(start);
            setEndDate(end);
          }}
        />
        <FieldText
          label={t("energy_mgmt.horizon")}
          type="number"
          min={1}
          value={horizon}
          onChange={(v) => setHorizon(Number(v))}
        />
        <FieldText
          label={t("energy_mgmt.delta_t")}
          type="number"
          min={0.1}
          step={0.1}
          value={deltaT}
          onChange={(v) => setDeltaT(Number(v))}
        />
      </div>
      <BusinessValidityChangeNotice
        enabled={isEditMode}
        originalStartDate={config?.start_date || initialStartDate}
        originalEndDate={
          config?.end_date || defaultBusinessEndDate(config?.start_date || initialStartDate)
        }
        startDate={startDate}
        endDate={endDate}
        className="mt-[14px]"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px] mt-[14px]">
        <ToggleRow
          label={t("energy_mgmt.strict_pos")}
          hint={t("energy_mgmt.strict_pos_hint")}
          on={strictPosCoeffs}
          onToggle={() => setStrictPosCoeffs((s) => !s)}
        />
        <ToggleRow
          label={t("energy_mgmt.sum_to_one")}
          hint={t("energy_mgmt.sum_to_one_hint")}
          on={totalShareCoeffs}
          onToggle={() => setTotalShareCoeffs((s) => !s)}
        />
      </div>

      {/* Module-specific settings */}
      {Object.keys(properties).length > 0 && (
        <>
          <div className="rec-divider" />
          <div className="rec-ems-section-title">{t("unified_ems.module_specific_settings")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px] items-end">
            {Object.entries(properties)
              .filter(([, prop]) => prop.type !== "boolean")
              .map(([key, prop]) => renderField(key, prop))}
          </div>
          {Object.entries(properties).some(([, prop]) => prop.type === "boolean") && (
            <div className="flex flex-col gap-[14px] mt-[14px]">
              {Object.entries(properties)
                .filter(([, prop]) => prop.type === "boolean")
                .map(([key, prop]) => renderField(key, prop))}
            </div>
          )}
        </>
      )}

      {/* Actions */}
      <div className="rec-divider" />
      <div className="flex items-center justify-end gap-[10px] flex-wrap">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          {t("unified_ems.cancel")}
        </Button>
        {isEditMode && (
          <Button variant="ghost" className="!text-danger" onClick={handleDelete} disabled={busy}>
            <Icon name="delete" />
            {isDeleting ? t("unified_ems.deleting") : t("unified_ems.delete")}
          </Button>
        )}
        <Button onClick={handleSave} disabled={busy}>
          <Icon name="save" />
          {(() => {
            if (isCreating || isUpdating) return t("unified_ems.saving");
            if (isEditMode) return t("unified_ems.update");
            return t("unified_ems.activate");
          })()}
        </Button>
      </div>
    </div>
  );
}

ModuleConfigForm.propTypes = {
  communityId: PropTypes.string.isRequired,
  moduleType: PropTypes.string.isRequired,
  config: PropTypes.shape({
    id: PropTypes.string,
    module_type: PropTypes.string,
    horizon: PropTypes.number,
    delta_t: PropTypes.number,
    strict_pos_coeffs: PropTypes.bool,
    total_share_coeffs: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    config_data: PropTypes.object, // Dynamic JSONB field - structure varies by module
    is_active: PropTypes.bool,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
  }),
  isEditMode: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
};

ModuleConfigForm.defaultProps = {
  config: null,
  onCancel: null,
  onSuccess: null,
};

export default ModuleConfigForm;
